import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';

interface FormData {
  item: string;
}

interface InclusionExclusionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string;
  type: 'inclusion' | 'exclusion';
  onSuccess?: () => void;
}

export function InclusionExclusionModal({
  open,
  onOpenChange,
  packageId,
  type,
  onSuccess,
}: InclusionExclusionModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isInclusion = type === 'inclusion';

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: { item: '' },
  });

  const utils = trpc.useUtils();

  const addInclusionMutation = trpc.package.addInclusion.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const addExclusionMutation = trpc.package.addExclusion.useMutation({
    onSuccess: () => {
      utils.package.byId.invalidate(packageId);
      reset();
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: FormData) => {
    setIsSubmitting(true);

    if (isInclusion) {
      addInclusionMutation.mutate({
        packageId,
        item: data.item,
      });
    } else {
      addExclusionMutation.mutate({
        packageId,
        item: data.item,
      });
    }
  };

  const error = isInclusion ? addInclusionMutation.error : addExclusionMutation.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add {isInclusion ? 'Inclusion' : 'Exclusion'}</DialogTitle>
          <DialogDescription>
            Add an item that is {isInclusion ? 'included in' : 'not included in'} this package.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="item">Item *</Label>
            <Input
              id="item"
              {...register('item', { required: 'Item is required' })}
              placeholder={
                isInclusion
                  ? 'e.g., Airport transfers, Breakfast daily'
                  : 'e.g., International flights, Travel insurance'
              }
            />
            {errors.item && (
              <p className="text-sm text-red-500">{errors.item.message}</p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add {isInclusion ? 'Inclusion' : 'Exclusion'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
